import './DataRetentionPolicies.scss'
import React, { useState } from 'react'
import { useDocumentTitle } from '../../../common/useDocumentTitle'
import { Col, Container, Dropdown, Row } from 'react-bootstrap'
import { Button } from '../../../components/Button/Button'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useGetRetentionPoliciesQuery } from '../../../store/account/account'
import { TableColumn } from 'react-data-table-component'
import { ActionButton } from '../../../components/DataTable/ActionButton'
import {
  AuditLogRetention,
  EventRetentionRule,
  FileRetention,
  RetentionPolicy,
  RetentionResourceType,
  RetentionRuleType,
  SessionRetention,
  TTLRetentionRule,
} from '../../../models/RetentionPolicy'
import { RetentionPolicyTTLConfigView } from './RetentionPolicyTTLRuleBody'
import { CustomDropdownItem } from '../../../components/Dropdown/CustomDropdownItem'

import { CreateOrEditPolicyModal } from './CreateOrEditPolicyModal'
import { DeletePolicyModal } from './DeletePolicyModal'
import { ViewPolicyModal } from './ViewPolicyModal'
import { RetentionPolicyRuleType } from './RetentionPolicyRuleType'
import { RetentionPolicyResourceType } from './RetentionPolicyResourceType'
import { PIIRedactionConfig } from './PIIRedactionConfig'
import { AuditLogSettings } from './AuditLogSettings'
import { getAuditLogKindLabel } from './auditLogKindLabels'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

const _ALL_RESOURCE_MESSAGE = 'ALL'

const fileKinds: { [key: string]: string } = {
  attachment: 'Attachment',
  recording: 'Meeting',
  transcription: 'Transcription',
}

const getFileKindLabel = (kind?: string | null) => fileKinds[kind ?? ''] || kind

export const DataRetentionPolicies: React.FC = () => {
  useDocumentTitle('Setup')
  const { hasQuota } = useZocksQuota()
  const accountId = useAppSelector(selectAccountId)
  const { data: retentionPoliciesData } = useGetRetentionPoliciesQuery({ accountId })
  const auditLogQuotaAvailable = hasQuota('auth.auditLogEnabled.value')
  const isEnterpriseManaged = retentionPoliciesData?.data?.some((policy) => policy.enterprise) || false

  return (
    <Container fluid className='p-0'>
      <Row className='mb-3'>
        <Col>
          <div className='lead-medium'>Audit logs</div>
        </Col>
      </Row>
      <Row className='zocks-mb-2.5'>
        <Col>
          {auditLogQuotaAvailable && !isEnterpriseManaged ? (
            <AuditLogSettings />
          ) : isEnterpriseManaged ? (
            <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
              <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                <div className='d-flex align-self-center gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>Audit log settings are managed at the enterprise level.</span>
                </div>
              </Col>
            </Row>
          ) : (
            <Row className='p-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
              <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
                <div className='d-flex align-self-center gap-3 align-items-center'>
                  <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
                  <span className='zocks-text-gray-900'>
                    Audit logs track account activity for compliance and security. Enable searchable logs for instant access to query and search logs, or
                    archived logs for extended retention.
                  </span>
                </div>
                <ScopedBlock
                  scope={{
                    scope: 'account:update',
                    superScope: ['account:admin'],
                  }}
                  quota={{
                    quota: 'auth.auditLogEnabled.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant='primary'>Upgrade</Button>
                </ScopedBlock>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
      <Row className='mb-3 mt-5'>
        <Col>
          <div className='lead-medium'>Defaults</div>
        </Col>
      </Row>
      <Row className='zocks-mb-2.5'>
        <Col>
          <DefaultRetentionPolicies auditLogQuotaAvailable={auditLogQuotaAvailable} />
        </Col>
      </Row>
      <Row className='mb-3'>
        <Col>
          <div className='lead-medium'>Custom Policies</div>
        </Col>
      </Row>
      <Row>
        <Col>
          <CustomRetentionPolicies auditLogQuotaAvailable={auditLogQuotaAvailable} />
        </Col>
      </Row>
      <Row>
        <Col>
          <PIIRedactionConfig />
        </Col>
      </Row>
    </Container>
  )
}

export const DefaultRetentionPolicies: React.FC<{ auditLogQuotaAvailable?: boolean }> = ({ auditLogQuotaAvailable = true }) => {
  const [showEditPolicyModal, setShowEditPolicyModal] = useState<boolean>(false)
  const accountId = useAppSelector(selectAccountId)
  const { data } = useGetRetentionPoliciesQuery({ accountId: accountId })
  const isEnterpriseManaged = data?.data.some((policy) => policy.enterprise) || false
  const handleEditPolicyModalHide = () => {
    setShowEditPolicyModal(false)
  }

  const handleAddCustomPolicy = () => {
    setShowEditPolicyModal(true)
  }
  return (
    <Container className='data-retention-defaults-container zocks-py-1.25 px-4'>
      <CreateOrEditPolicyModal show={showEditPolicyModal} onHide={handleEditPolicyModalHide} auditLogQuotaAvailable={auditLogQuotaAvailable} />
      {isEnterpriseManaged ? (
        <Row className='p-3 mb-3 zocks-bg-color-primary-light gap-3 justify-content-center rounded'>
          <Col className='d-flex align-items-center zocks-gap-2.5 align-self-center'>
            <div className='d-flex align-self-center gap-3 align-items-center'>
              <i className='bi bi-info-square zocks-text-primary consent-settings-info-square' />
              <span className='zocks-text-gray-900'>Retention policies are managed at the enterprise level.</span>
            </div>
          </Col>
        </Row>
      ) : null}
      <Row className='gy-3'>
        <Col className='p-0'>
          <Container>
            <Row className='zocks-mb-1.25'>
              <Col>
                <h4>Retention Policies</h4>
              </Col>
            </Row>
            <Row className='mb-3'>
              <Col>
                <div className='paragraph-small'>
                  Data retention policies define how long we retain your data. You can override these policies by clicking on "Add Custom Policy" if needed.
                </div>
                <div className='paragraph-small mt-2'>
                  By default, we retain all your data for 2 years, except audit logs which have shorter default periods. searchable logs (7 days) and archived
                  logs (90 days).
                </div>
              </Col>
            </Row>
            <Row className='mt-5'>
              <Col>
                <ScopedBlock
                  quota={{
                    quota: 'retention.enabled.value',
                  }}
                  missingScopeAction={MissingScopeAction.UPGRADE}
                >
                  <Button variant='primary' onClick={handleAddCustomPolicy} disabled={isEnterpriseManaged}>
                    <i className='bi bi-plus' />
                    Add custom policy
                  </Button>
                </ScopedBlock>
              </Col>
            </Row>
          </Container>
        </Col>
      </Row>
    </Container>
  )
}

const CustomRetentionPolicies: React.FC<{ auditLogQuotaAvailable?: boolean }> = ({ auditLogQuotaAvailable = true }) => {
  const accountId = useAppSelector(selectAccountId)
  const [selectedRetentionPolicyId, setSelectedRetentionPolicyId] = useState<string | null>()
  const [showEditPolicyModal, setShowEditPolicyModal] = useState<boolean>(false)
  const [showViewPolicyModal, setShowViewPolicyModal] = useState<boolean>(false)
  const [showDeletePolicyModal, setShowDeletePolicyModal] = useState<boolean>(false)
  const { data, isFetching } = useGetRetentionPoliciesQuery({ accountId: accountId }, { refetchOnMountOrArgChange: true })
  const isEnterpriseManaged = data?.data.some((policy) => policy.enterprise) || false

  const handleOnClickEditPolicy = (policy: RetentionPolicy) => {
    setSelectedRetentionPolicyId(policy.id)
    setShowEditPolicyModal(true)
  }

  const handleOnClickViewPolicy = (policy: RetentionPolicy) => {
    setSelectedRetentionPolicyId(policy.id)
    setShowViewPolicyModal(true)
  }

  const handleOnClickDeletePolicy = (policy: RetentionPolicy) => {
    setSelectedRetentionPolicyId(policy.id)
    setShowDeletePolicyModal(true)
  }

  const handleEditPolicyModalHide = () => {
    setShowEditPolicyModal(false)
    setSelectedRetentionPolicyId(null)
  }

  const handleViewPolicyModalHide = () => {
    setShowViewPolicyModal(false)
    setSelectedRetentionPolicyId(null)
  }

  const handleDeletePolicyModalHide = () => {
    setShowDeletePolicyModal(false)
    setSelectedRetentionPolicyId(null)
  }

  const ruleConfigTemplate = (rowData: RetentionPolicy) => {
    const { rule } = rowData
    if (rule.type === RetentionRuleType.TTL) {
      const { config } = rule as TTLRetentionRule
      return <RetentionPolicyTTLConfigView ttlConfig={config} />
    }

    if (rule.type === RetentionRuleType.EVENT) {
      const ttlRetentionRule = rule as EventRetentionRule
      return <div>{ttlRetentionRule.config.name}</div>
    }
  }

  const resourceConfigTemplate = (rowData: RetentionPolicy) => {
    const { resource } = rowData
    if (resource.type === RetentionResourceType.ROOM) {
      return <div>{_ALL_RESOURCE_MESSAGE}</div>
    }
    if (resource.type === RetentionResourceType.SESSION) {
      const { config } = resource as SessionRetention
      return (
        <>
          {config ? (
            <div className='d-flex flex-column gap-2'>
              <div>kind: {getFileKindLabel(config.kind) || _ALL_RESOURCE_MESSAGE}</div>
              <div>source: {config.source || _ALL_RESOURCE_MESSAGE}</div>
            </div>
          ) : (
            _ALL_RESOURCE_MESSAGE
          )}
        </>
      )
    }
    if (resource.type === RetentionResourceType.FILE) {
      const { config } = resource as FileRetention
      return <div>{getFileKindLabel(config?.kind) || _ALL_RESOURCE_MESSAGE}</div>
    }
    if (resource.type === RetentionResourceType.AUDIT_LOG) {
      const { config } = resource as AuditLogRetention
      return <div>{getAuditLogKindLabel(config?.kind) || _ALL_RESOURCE_MESSAGE}</div>
    }
  }

  const columns: TableColumn<RetentionPolicy>[] = [
    {
      name: 'Resource',
      selector: (row) => row.resource.type,
      cell: (row) => <RetentionPolicyResourceType type={row.resource.type} />,
      sortable: true,
    },
    {
      name: 'Resource Config',
      selector: (row) => row.rule.type,
      sortable: true,
      cell: resourceConfigTemplate,
    },
    {
      name: 'Rule Type',
      selector: (row) => row.rule.type,
      sortable: true,
      cell: (row) => <RetentionPolicyRuleType type={row.rule.type} />,
    },
    {
      name: 'Rule Config',
      selector: (row) => row.rule.type,
      sortable: true,
      cell: ruleConfigTemplate,
    },

    {
      ignoreRowClick: true,
      right: true,
      button: true,
      allowOverflow: true,
      width: '3rem',
      cell: (row) => (
        <ActionButton>
          <ScopedBlock
            scope={{
              scope: 'retention_policy:read',
              superScope: ['retention_policy:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickViewPolicy(row)}>
              Show
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'retention_policy:update',
              superScope: ['retention_policy:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
            authActions={[() => !isEnterpriseManaged]}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickEditPolicy(row)}>
              Edit
            </Dropdown.Item>
          </ScopedBlock>
          <ScopedBlock
            scope={{
              scope: 'retention_policy:delete',
              superScope: ['retention_policy:admin'],
            }}
            missingScopeAction={MissingScopeAction.DISABLE}
            authActions={[() => !isEnterpriseManaged]}
          >
            <Dropdown.Item as={CustomDropdownItem} onClick={() => handleOnClickDeletePolicy(row)}>
              Delete
            </Dropdown.Item>
          </ScopedBlock>
        </ActionButton>
      ),
    },
  ]

  const tableData =
    data?.data?.filter((policy) => {
      if (policy.isDefault) {
        return false
      }
      if (policy.resource.type === RetentionResourceType.AUDIT_LOG) {
        return (policy.resource as AuditLogRetention).config?.defaultEnabled === null
      }
      return true
    }) ?? []

  return (
    <Container fluid className='p-0'>
      {selectedRetentionPolicyId ? (
        <CreateOrEditPolicyModal
          policyId={selectedRetentionPolicyId}
          show={showEditPolicyModal}
          onHide={handleEditPolicyModalHide}
          auditLogQuotaAvailable={auditLogQuotaAvailable}
        />
      ) : null}
      {selectedRetentionPolicyId ? (
        <ViewPolicyModal policyId={selectedRetentionPolicyId} show={showViewPolicyModal} onHide={handleViewPolicyModalHide} />
      ) : null}
      {selectedRetentionPolicyId ? (
        <DeletePolicyModal
          policy={data?.data.find((policy) => policy.id === selectedRetentionPolicyId)}
          show={showDeletePolicyModal}
          onHide={handleDeletePolicyModalHide}
        />
      ) : null}
      <Row>
        <Col>
          <ZocksDataTable
            columns={columns}
            data={tableData}
            progressPending={isFetching}
            noDataComponent={<div className='d-flex w-100 justify-content-start justify-content-md-center'>There are no custom policies.</div>}
          />
        </Col>
      </Row>
    </Container>
  )
}
